import React, { useMemo } from 'react';
import { Icon } from '@iconify/react';
import { Topic } from '../../../../types/topic';
import { SearchFilters } from '../../../../types/search';
import { Checkbox } from '../../../common/Checkbox';
import { useDirection } from '../../../../contexts/DirectionContext';

interface TopicsSectionProps {
  topicSearch: string;
  setTopicSearch: (value: string) => void;
  filteredTopics: Topic[];
  numTopicsShown: number;
  draftFilters: SearchFilters;
  hasMoreTopics: boolean;
  hasLessTopics: boolean;
  onTopicToggle: (topicId: string) => void;
  onShowMore: () => void;
  onShowLess: () => void;
  t: (key: string) => string;
}

export const TopicsSection: React.FC<TopicsSectionProps> = ({
  topicSearch,
  setTopicSearch,
  filteredTopics,
  numTopicsShown,
  draftFilters,
  hasMoreTopics,
  hasLessTopics,
  onTopicToggle,
  onShowMore,
  onShowLess,
  t
}) => {
  const { isRTL } = useDirection();

  const sortedTopics = useMemo(() => {
    // Create a map to maintain original indices
    const indexMap = new Map(filteredTopics.map((topic, index) => [topic.id, index]));
    
    return [...filteredTopics].sort((a, b) => {
      const aSelected = draftFilters.topics.includes(a.id);
      const bSelected = draftFilters.topics.includes(b.id);
      
      if (aSelected === bSelected) {
        // If both selected or both unselected, maintain original order
        return indexMap.get(a.id)! - indexMap.get(b.id)!;
      }
      // Selected topics go first
      return aSelected ? -1 : 1;
    });
  }, [filteredTopics, draftFilters.topics]);

  const visibleTopics = sortedTopics.slice(0, numTopicsShown);

  return (
    <>
      <div className="relative mb-3 px-1">
        <input
          type="text"
          value={topicSearch}
          onChange={(e) => setTopicSearch(e.target.value)}
          placeholder={t("search.filters.searchTopics")}
          className={`w-full px-4 py-2 ${
            isRTL ? "pr-10" : "pl-10"
          } text-text-primary bg-surface-secondary border border-border-primary rounded-full text-sm placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-shadow`}
        />
        <Icon
          icon="ph:magnifying-glass-thin"
          className={`absolute top-1/2 -translate-y-1/2 ${
            isRTL ? "right-3" : "left-3"
          } w-4 h-4 text-text-primary opacity-30`}
        />
      </div>
      <div className="px-1">
        {visibleTopics.map((topic) => (
          <div key={topic.id} className="flex min-w-0 py-0.5">
            <div className="flex-1 min-w-0 break-words whitespace-normal leading-tight">
              <Checkbox
                checked={draftFilters.topics.includes(topic.id)}
                label={topic.title}
                onClick={() => onTopicToggle(topic.id)}
                truncate={false}
              />
            </div>
            {topic.count !== undefined && (
              <span
                className={`text-xs text-text-secondary opacity-70 whitespace-nowrap flex-shrink-0 ${
                  isRTL ? "mr-2" : "ml-2"
                } py-1`}
              >
                ({topic.count})
              </span>
            )}
          </div>
        ))}
        <div className="flex justify-center mt-2 gap-1 items-center">
          {hasMoreTopics && (
            <button
              onClick={onShowMore}
              className="text-xs text-text-secondary hover:text-text-primary font-medium px-2 py-1 hover:bg-surface-tertiary rounded-md transition-colors"
            >
              {t("common.seeMore")}
            </button>
          )}
          {hasMoreTopics && hasLessTopics && (
            <span className="text-xs text-text-secondary opacity-30">•</span>
          )}
          {hasLessTopics && (
            <button
              onClick={onShowLess}
              className="text-xs text-text-secondary hover:text-text-primary font-medium px-2 py-1 hover:bg-surface-tertiary rounded-md transition-colors"
            >
              {t("common.seeLess")}
            </button>
          )}
        </div>
      </div>
    </>
  );
};
